import { Close } from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import { Box, Button, Checkbox, Drawer, FormControlLabel, IconButton, MenuItem, Modal, Paper, TextField, Typography, useMediaQuery, useTheme } from "@mui/material";
import { Fragment, ReactNode, useState } from "react";
import { GobikesApi } from "../../config/Apis";
import { getAuthCookie } from "../auth/AuthUtil";
import { toIstDateString } from "../utils/CommonUtils";
import { OrderModel, PaymentMode, VehicleType } from "../utils/Models";

interface FormState {
  loading?: boolean;
  error?: boolean;
  message?: string;
}

// Same reasons as the admin cancel booking page - values must match the server's CancellationReason enum
const OTHERS_REASON = 'OTHERS';
const cancelReasons = [
  { label: 'Vehicle is not available', value: 'VEHICLE_NOT_AVAILABLE' },
  { label: 'Vehicle is not in proper condition', value: 'VEHICLE_CONDITION_NOT_PROPER' },
  { label: 'Documentation Issue', value: 'DOCUMENTATION_ISSUE' },
  { label: 'Plan got changed/canceled', value: 'PLAN_CHANGED_OR_CANCELLED' },
  { label: 'Pickup Location is too far', value: 'PICKUP_LOCATION_TOO_FAR' },
  { label: 'Shop is closed', value: 'SHOP_IS_CLOSED' },
  { label: 'Wrong vehicle booked', value: 'WRONG_VEHICLE_BOOKED' },
  { label: 'Wrong date booked', value: 'WRONG_DATE_BOOKED' },
  { label: 'Partner is not reachable/available', value: 'PARTNER_NOT_REACHABLE' },
  { label: "Customer didn't come", value: 'CUSTOMER_DID_NOT_COME' },
  { label: "Customer didn't like vehicle", value: 'CUSTOMER_DID_NOT_LIKE_VEHICLE' },
  { label: 'Outstation travel is not allowed', value: 'OUTSTATION_TRAVEL_NOT_ALLOWED' },
  { label: 'Delivery is not possible', value: 'DELIVERY_NOT_POSSIBLE' },
  { label: 'Others', value: OTHERS_REASON }
];

// Cancel booking after its pickup time - no refund is initiated to the customer
export function CancelBooking(props: {
  children: ReactNode;
  order: OrderModel;
  style?: any;
  backFn: () => void;
}) {
  const [open, setOpen]           = useState(false);
  const [reason, setReason]       = useState<string>('');
  const [comment, setComment]     = useState<string>('');
  const [makeLive, setMakeLive]   = useState<boolean>(false);
  const [formState, setFormState] = useState<FormState>({});

  const authCookie = getAuthCookie();
  const theme = useTheme();
  const isPhone = !useMediaQuery(theme.breakpoints.up("md"));
  const vehicleLabel = props.order.bikes?.[0]?.vehicleType === VehicleType.CAR ? 'car' : 'bike';

  function handleOpen() {
    setReason('');
    setComment('');
    setMakeLive(false);
    setFormState({});
    setOpen(true);
  }

  function handleClose() { setOpen(false); }

  async function handleSubmit(e: any) {
    e.preventDefault();

    if (!reason) {
      setFormState({ error: true, message: 'Select a reason for cancellation' });
      return;
    }
    if (reason === OTHERS_REASON && !comment.trim()) {
      setFormState({ error: true, message: 'Describe the reason for cancellation' });
      return;
    }

    setFormState({ loading: true });
    try {
      await GobikesApi.post(
        `/vendors/hubs/${props.order.hubId}/orders/${props.order.id}/cancel`,
        { reason, ...(reason === OTHERS_REASON && { comment: comment.trim() }), block: !makeLive },
        { headers: { 'vendor-token': authCookie } }
      );
      // Keep the button in loading state until the page reloads, so the booking can't be submitted twice
      setFormState({ error: false, message: 'Booking cancelled!', loading: true });
      setTimeout(() => { handleClose(); props.backFn(); }, 2000);
    } catch (e: any) {
      const errorMessage = (e.response?.data?.message) ? 'Error: ' + e.response.data.message : 'An error occurred';
      setFormState({ error: true, message: errorMessage, loading: false });
    }
  }

  const fieldSx = { mb: 1.5, '& .MuiOutlinedInput-root': { borderRadius: '10px', fontSize: '14px' }, '& label': { fontSize: '13px' } };

  const infoRows = [
    { label: 'Booking Date & Time', value: toIstDateString(new Date(props.order.createdAt).getTime()) },
    { label: 'Pickup Date & Time', value: toIstDateString(props.order.rideStartScheduled) },
    { label: 'Order Mode', value: props.order.orderMode },
    { label: 'Payment Mode', value: props.order.paymentMode },
    { label: 'Amount Paid by Customer', value: `₹ ${Number(props.order.amountPayableOnline || 0).toFixed(2)}` },
    {
      label: props.order.paymentMode !== PaymentMode.ONLINE ? 'Amount to be paid to you' : 'Amount paid to you',
      value: `₹ ${Number(props.order.vendorAmount || 0).toFixed(2)}`
    }
  ];

  const content = (
    <Fragment>

      {/* Booking and payment info */}
      <Box sx={{ border: '1px solid #f0f0f0', borderRadius: '10px', mb: 1.5 }}>
        {infoRows.map((row, i) => (
          <Box
            key={row.label}
            sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, px: 1.5, py: 1, ...(i > 0 && { borderTop: '1px solid #f0f0f0' }) }}
          >
            <Typography sx={{ fontSize: '12px', color: '#888' }}>{row.label}</Typography>
            <Typography className="roboto" sx={{ fontSize: '13px', fontWeight: 600, textAlign: 'right' }}>{row.value}</Typography>
          </Box>
        ))}
      </Box>

      {/* No refund note */}
      <Box sx={{ background: '#fff8e1', border: '1px solid #ffe082', borderRadius: '10px', px: 1.5, py: 1, mb: 2 }}>
        <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#8a6d00' }}>
          No refund will be initiated to the customer on cancellation.
        </Typography>
      </Box>

      {/* Reason */}
      <TextField
        select
        label="Reason for cancellation"
        size="small"
        fullWidth
        required
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        sx={fieldSx}
      >
        {cancelReasons.map((r) => (
          <MenuItem key={r.value} value={r.value} sx={{ fontSize: '14px' }}>{r.label}</MenuItem>
        ))}
      </TextField>
      {reason === OTHERS_REASON && (
        <TextField
          label="Reason Description"
          size="small"
          fullWidth
          required
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          sx={fieldSx}
        />
      )}

      {/* Make vehicle live again */}
      <FormControlLabel
        control={<Checkbox size="small" checked={makeLive} onChange={(e) => setMakeLive(e.target.checked)} />}
        label={<Typography sx={{ fontSize: '13px' }}>Make {vehicleLabel} live again</Typography>}
        sx={{ mb: 0.5 }}
      />

      {formState.message && (
        <Typography sx={{ fontSize: '12px', color: formState.error ? '#c0392b' : '#27ae60', mt: 0.5, mb: 0.5 }}>
          {formState.message}
        </Typography>
      )}
    </Fragment>
  );

  return (
    <Fragment>
      <span onClick={handleOpen} style={props.style}>
        {props.children}
      </span>

      {isPhone ? (
        <Drawer
          anchor="bottom"
          open={open}
          onClose={handleClose}
          PaperProps={{ sx: { borderTopLeftRadius: 18, borderTopRightRadius: 18, padding: '20px 20px 40px', maxHeight: '90vh' } }}
        >
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 700, fontSize: '16px' }}>Cancel Booking</Typography>
          {content}
          <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
            <LoadingButton
              variant="contained"
              loading={Boolean(formState.loading)}
              sx={{ flex: 1, borderRadius: '10px', textTransform: 'none', fontWeight: 700 }}
              onClick={handleSubmit}
            >
              Cancel Booking
            </LoadingButton>
            <Button
              variant="outlined"
              sx={{ borderRadius: '10px', textTransform: 'none' }}
              onClick={handleClose}
            >
              Back
            </Button>
          </Box>
        </Drawer>
      ) : (
        <Modal open={open} className="gbk-modal" onClose={handleClose}>
          <Box className="gbk-modal-content" style={{ width: 'min(440px, calc(100vw - 32px))', height: 'auto', maxHeight: 'calc(100vh - 80px)', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', position: 'absolute', overflowY: 'auto' }}>
            <Paper className="gbk-paper" sx={{ borderRadius: '16px !important', overflow: 'hidden', height: 'auto !important' }}>

              {/* Header */}
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2.5, py: 1.5, borderBottom: '1px solid #f0f0f0' }}>
                <Typography sx={{ fontSize: '15px', fontWeight: 700 }}>Cancel Booking</Typography>
                <IconButton onClick={handleClose} size="small" sx={{ color: '#666' }}>
                  <Close fontSize="small" />
                </IconButton>
              </Box>

              {/* Form */}
              <Box sx={{ px: 2.5, pt: 2, pb: 1 }}>
                {content}
              </Box>

              {/* Footer */}
              <Box sx={{ px: 2.5, pb: 2.5 }}>
                <form onSubmit={handleSubmit}>
                  <LoadingButton
                    variant="contained"
                    type="submit"
                    fullWidth
                    loading={formState.loading}
                    sx={{ borderRadius: '12px', textTransform: 'none', fontWeight: 700, fontSize: '14px', py: 1.2, background: '#8dbe2e', '&:hover': { background: '#78a225' } }}
                  >
                    Cancel Booking
                  </LoadingButton>
                </form>
              </Box>

            </Paper>
          </Box>
        </Modal>
      )}
    </Fragment>
  );
}
